import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Switch, Chip, Alert, TextField,
  CircularProgress, Divider, Grid, FormControlLabel, Radio, RadioGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Tab, Tabs, Dialog, DialogTitle, DialogContent,
  DialogActions, alpha, Checkbox, FormGroup
} from '@mui/material';
import {
  Security as SecurityIcon, PhoneAndroid, Email, QrCode2, Lock, LockOpen,
  Shield, History, PersonOff, Refresh, ContentCopy, CheckCircle,
  Warning, AdminPanelSettings, Settings as SettingsIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { twofaAPI, securityAPI, profileAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';


const Security = () => {
  const { user, refreshUser } = useAuth();
  const isAdmin = user?.isSuperAdmin || user?.role === 'super_admin' || user?.role === 'admin';

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);

  // 2FA State
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [securityInfo, setSecurityInfo] = useState(null);

  // Admin state
  const [settings, setSettings] = useState(null);
  const [usersWithout2FA, setUsersWithout2FA] = useState([]);
  const [auditLog, setAuditLog] = useState({ logs: [], total: 0, page: 1 });
  const [settingsLoading, setSettingsLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const [statusRes, secRes] = await Promise.all([
        twofaAPI.getStatus(),
        profileAPI.getSecurity()
      ]);
      setStatus(statusRes.data);
      setSecurityInfo(secRes.data);
    } catch (err) {
      console.error('Failed to load 2FA status', err);
    }
  }, []);

  const loadAdminData = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [settingsRes, usersRes, auditRes] = await Promise.all([
        securityAPI.getSettings(),
        securityAPI.getUsersWithout2FA(),
        securityAPI.getAuditLog({ page: 1, limit: 50 })
      ]);
      setSettings(settingsRes.data);
      setUsersWithout2FA(usersRes.data);
      setAuditLog(auditRes.data);
    } catch (err) {
      console.error('Failed to load admin data', err);
    }
  }, [isAdmin]);

  useEffect(() => { document.title = 'Security | MyPayPlus'; }, []);
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadStatus();
      await loadAdminData();
      setLoading(false);
    };
    init();
  }, [loadStatus, loadAdminData]);

  // ─── TOTP Setup Flow ────────────────────────────────
  const handleSetupTotp = async () => {
    setSetupLoading(true);
    try {
      const res = await twofaAPI.setupTotp();
      setSetupData(res.data);
      setConfirmCode('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start setup');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleConfirmTotp = async () => {
    if (!confirmCode || confirmCode.length < 6) {
      toast.error('Enter a valid 6-digit code'); return;
    }
    setSetupLoading(true);
    try {
      await twofaAPI.confirmTotp({ code: confirmCode });
      toast.success('Authenticator app enabled successfully!');
      setSetupData(null);
      setConfirmCode('');
      await loadStatus();
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid code');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleDisableTotp = async () => {
    if (!window.confirm('Are you sure you want to disable authenticator app 2FA?')) return;
    try {
      await twofaAPI.disableTotp();
      toast.success('Authenticator app 2FA disabled');
      setSetupData(null);
      await loadStatus();
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to disable');
    }
  };

  const handleSetPreferred = async (method) => {
    try {
      await twofaAPI.setPreferred({ method });
      toast.success(`Preferred method set to ${method === 'totp' ? 'Authenticator App' : 'Email'}`);
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    }
  };

  // ─── Admin Settings ─────────────────────────────────
  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      await securityAPI.updateSettings(settings);
      toast.success('Security settings saved');
      await loadAdminData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleUnlockUser = async (userId) => {
    try {
      await securityAPI.unlockUser(userId);
      toast.success('User account unlocked');
    } catch (err) {
      toast.error('Failed to unlock user');
    }
  };

  const handleAdminDisable2FA = async (userId) => {
    if (!window.confirm('Disable 2FA for this user?')) return;
    try {
      await twofaAPI.adminDisableTotp(userId);
      toast.success('2FA disabled for user');
      await loadAdminData();
    } catch (err) {
      toast.error('Failed to disable 2FA');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.info('Copied to clipboard');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 3,
          background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <SecurityIcon sx={{ color: '#fff', fontSize: 26 }} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>Security</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage two-factor authentication and security settings
          </Typography>
        </Box>
      </Box>

      {user?.twoFaRequired && !user?.totpEnabled && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2.5, fontWeight: 600 }}>
          Your administrator requires you to set up two-factor authentication before you can access the system. Please set up an authenticator app below.
        </Alert>
      )}

      <Tabs
        value={tab} onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' } }}
      >
        <Tab icon={<Shield sx={{ fontSize: 18 }} />} iconPosition="start" label="My 2FA" />
        {isAdmin && <Tab icon={<AdminPanelSettings sx={{ fontSize: 18 }} />} iconPosition="start" label="Admin Settings" />}
        {isAdmin && <Tab icon={<History sx={{ fontSize: 18 }} />} iconPosition="start" label="Audit Log" />}
      </Tabs>

      {/* ════════ TAB 0: My 2FA ════════ */}
      {tab === 0 && (
        <Grid container spacing={3}>
          {/* 2FA Status Overview */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Shield sx={{ color: '#3b82f6' }} />
                  <Typography variant="h6">Two-Factor Authentication</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  {status?.totpEnabled ? (
                    <Chip icon={<CheckCircle />} label="Enabled" color="success" size="small" />
                  ) : (
                    <Chip icon={<Warning />} label="Not Set Up" color="warning" size="small" />
                  )}
                </Box>

                {status?.twoFactorRequired && !status?.totpEnabled && (
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    Your administrator requires 2FA. Please set up an authenticator app.
                  </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Preferred Method</Typography>
                  <Chip
                    icon={status?.preferredMethod === 'totp' ? <PhoneAndroid /> : <Email />}
                    label={status?.preferredMethod === 'totp' ? 'Authenticator App' : 'Email'}
                    size="small" variant="outlined"
                  />
                </Box>

                {status?.totpEnabled && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Preferred Verification Method</Typography>
                    <RadioGroup
                      value={status?.preferredMethod || 'email'}
                      onChange={(e) => handleSetPreferred(e.target.value)}
                    >
                      {status?.availableMethods?.includes('email') && (
                        <FormControlLabel value="email" control={<Radio size="small" />}
                          label={<Typography variant="body2">Email verification code</Typography>} />
                      )}
                      {status?.availableMethods?.includes('totp') && (
                        <FormControlLabel value="totp" control={<Radio size="small" />}
                          label={<Typography variant="body2">Authenticator app (Google Authenticator)</Typography>} />
                      )}
                    </RadioGroup>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />
                {!status?.totpEnabled ? (
                  <Button
                    variant="contained" fullWidth startIcon={<QrCode2 />}
                    onClick={handleSetupTotp} disabled={setupLoading}
                    sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}
                  >
                    Set Up Authenticator App
                  </Button>
                ) : (
                  <Button
                    variant="outlined" color="error" fullWidth startIcon={<LockOpen />}
                    onClick={handleDisableTotp}
                  >
                    Disable Authenticator App
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* QR Code Setup / Security Info */}
          <Grid item xs={12} md={6}>
            {setupData ? (
              <Card sx={{ border: '2px solid', borderColor: 'primary.main' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <QrCode2 sx={{ color: '#3b82f6' }} />
                    <Typography variant="h6">Set Up Authenticator</Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    Scan this QR code with Google Authenticator, Authy, or any TOTP app.
                  </Alert>

                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <img src={setupData.qrCode} alt="QR Code" style={{ width: 200, height: 200 }} />
                  </Box>

                  <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha('#000', 0.04), borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Manual entry key:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                        {setupData.secret}
                      </Typography>
                      <IconButton size="small" onClick={() => copyToClipboard(setupData.secret)}>
                        <ContentCopy sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <TextField
                    fullWidth label="Enter 6-digit code" value={confirmCode}
                    onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000" sx={{ mb: 2 }}
                    inputProps={{ maxLength: 6, style: { letterSpacing: 8, textAlign: 'center', fontSize: '1.2rem', fontWeight: 700 } }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmTotp(); }}
                  />

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={() => setSetupData(null)} sx={{ flex: 1 }}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained" onClick={handleConfirmTotp}
                      disabled={confirmCode.length < 6 || setupLoading}
                      sx={{ flex: 1, background: 'linear-gradient(135deg, #10b981, #059669)' }}
                    >
                      {setupLoading ? <CircularProgress size={20} /> : 'Verify & Enable'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Lock sx={{ color: '#8b5cf6' }} />
                    <Typography variant="h6">Security Info</Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Last Login</Typography>
                      <Typography variant="body2">
                        {securityInfo?.lastLogin ? new Date(securityInfo.lastLogin).toLocaleString() : 'Never'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Password Changed</Typography>
                      <Typography variant="body2">
                        {securityInfo?.passwordChangedAt ? new Date(securityInfo.passwordChangedAt).toLocaleString() : 'Never'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Account Created</Typography>
                      <Typography variant="body2">
                        {securityInfo?.accountCreated ? new Date(securityInfo.accountCreated).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                  </Box>

                  {securityInfo?.recentLogins?.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent Login Activity</Typography>
                      <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                        {securityInfo.recentLogins.map((login, i) => (
                          <Box key={i} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(login.date).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled' }}>
                              IP: {login.ip}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* How It Works / Info Card */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: alpha('#3b82f6', 0.04), border: '1px solid', borderColor: alpha('#3b82f6', 0.15) }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 1.5 }}>How Two-Factor Authentication Works</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Email sx={{ color: '#3b82f6', mt: 0.3 }} />
                      <Box>
                        <Typography variant="subtitle2">Email Verification</Typography>
                        <Typography variant="body2" color="text.secondary">
                          A one-time code is sent to your email address when you log in. Available by default.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <PhoneAndroid sx={{ color: '#10b981', mt: 0.3 }} />
                      <Box>
                        <Typography variant="subtitle2">Authenticator App</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Use Google Authenticator or similar apps for time-based codes. More secure and doesn&apos;t require email.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Shield sx={{ color: '#8b5cf6', mt: 0.3 }} />
                      <Box>
                        <Typography variant="subtitle2">Why Use 2FA?</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Added security layer prevents unauthorized access even if your password is compromised.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ════════ TAB 1: Admin Settings ════════ */}
      {tab === 1 && isAdmin && settings && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <SettingsIcon sx={{ color: '#3b82f6' }} />
                  <Typography variant="h6">2FA Settings</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle2">Enable Two-Factor Authentication</Typography>
                    <Typography variant="caption" color="text.secondary">
                      When disabled, users can log in with just username/password
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings['2fa_enabled'] !== false}
                    onChange={(e) => setSettings({ ...settings, '2fa_enabled': e.target.checked })}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle2">Require 2FA for All Users</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Force users to set up 2FA before they can use the system
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings['2fa_required'] === true}
                    onChange={(e) => setSettings({ ...settings, '2fa_required': e.target.checked })}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Allowed 2FA Methods</Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={settings['2fa_methods']?.includes('email') ?? true}
                        onChange={(e) => {
                          const methods = [...(settings['2fa_methods'] || ['email', 'totp'])];
                          if (e.target.checked) { if (!methods.includes('email')) methods.push('email'); }
                          else { const i = methods.indexOf('email'); if (i > -1) methods.splice(i, 1); }
                          setSettings({ ...settings, '2fa_methods': methods });
                        }}
                      />
                    }
                    label={<Typography variant="body2">Email OTP</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={settings['2fa_methods']?.includes('totp') ?? true}
                        onChange={(e) => {
                          const methods = [...(settings['2fa_methods'] || ['email', 'totp'])];
                          if (e.target.checked) { if (!methods.includes('totp')) methods.push('totp'); }
                          else { const i = methods.indexOf('totp'); if (i > -1) methods.splice(i, 1); }
                          setSettings({ ...settings, '2fa_methods': methods });
                        }}
                      />
                    }
                    label={<Typography variant="body2">Authenticator App (TOTP)</Typography>}
                  />
                </FormGroup>

                <Divider sx={{ my: 2 }} />
                <TextField
                  label="Max Login Attempts" type="number" size="small" fullWidth sx={{ mb: 2 }}
                  value={settings['max_login_attempts'] || 5}
                  onChange={(e) => setSettings({ ...settings, 'max_login_attempts': parseInt(e.target.value) || 5 })}
                  inputProps={{ min: 1, max: 20 }}
                  helperText="Account is locked after this many failed attempts"
                />
                <TextField
                  label="Session Timeout (hours)" type="number" size="small" fullWidth sx={{ mb: 2 }}
                  value={settings['session_timeout_hours'] || 24}
                  onChange={(e) => setSettings({ ...settings, 'session_timeout_hours': parseInt(e.target.value) || 24 })}
                  inputProps={{ min: 1, max: 720 }}
                  helperText="How long a login session remains valid"
                />
                <TextField
                  label="Remember Device Duration (days)" type="number" size="small" fullWidth sx={{ mb: 2 }}
                  value={settings['trusted_device_duration'] || 0}
                  onChange={(e) => setSettings({ ...settings, 'trusted_device_duration': parseInt(e.target.value) || 0 })}
                  inputProps={{ min: 0, max: 365 }}
                  helperText="0 = disabled. When set, users can skip OTP from the same IP for this many days"
                />

                <Button
                  variant="contained" fullWidth onClick={handleSaveSettings}
                  disabled={settingsLoading}
                  sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}
                >
                  {settingsLoading ? <CircularProgress size={20} /> : 'Save Settings'}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PersonOff sx={{ color: '#f59e0b' }} />
                    <Typography variant="h6">Users Without 2FA</Typography>
                  </Box>
                  <Chip label={usersWithout2FA.length} size="small" color="warning" />
                </Box>
                <Divider sx={{ mb: 2 }} />

                {usersWithout2FA.length === 0 ? (
                  <Alert severity="success" sx={{ borderRadius: 2 }}>
                    All active users have 2FA enabled!
                  </Alert>
                ) : (
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Last Login</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usersWithout2FA.map((u) => (
                          <TableRow key={u.id}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {u.firstName} {u.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={u.role} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Disable 2FA">
                                <IconButton size="small" color="error" onClick={() => handleAdminDisable2FA(u.id)}>
                                  <LockOpen sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ════════ TAB 2: Audit Log ════════ */}
      {tab === 2 && isAdmin && (
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <History sx={{ color: '#8b5cf6' }} />
                <Typography variant="h6">Security Audit Log</Typography>
              </Box>
              <Button size="small" startIcon={<Refresh />} onClick={async () => {
                const res = await securityAPI.getAuditLog({ page: 1, limit: 50 });
                setAuditLog(res.data);
              }}>
                Refresh
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <TableContainer sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLog.logs?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No audit events recorded yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : auditLog.logs?.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(log.createdAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.eventType}
                          size="small"
                          color={
                            log.eventType?.includes('SUCCESS') ? 'success' :
                            log.eventType?.includes('FAILED') || log.eventType?.includes('LOCKED') ? 'error' :
                            log.eventType?.includes('DISABLED') ? 'warning' : 'info'
                          }
                          variant="outlined"
                          sx={{ fontSize: '0.65rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.username || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {log.ipAddress || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {log.details || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {auditLog.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                <Button
                  size="small" disabled={auditLog.page <= 1}
                  onClick={async () => {
                    const res = await securityAPI.getAuditLog({ page: auditLog.page - 1, limit: 50 });
                    setAuditLog(res.data);
                  }}
                >Previous</Button>
                <Typography variant="body2" sx={{ lineHeight: '30px' }}>
                  Page {auditLog.page} of {auditLog.totalPages}
                </Typography>
                <Button
                  size="small" disabled={auditLog.page >= auditLog.totalPages}
                  onClick={async () => {
                    const res = await securityAPI.getAuditLog({ page: auditLog.page + 1, limit: 50 });
                    setAuditLog(res.data);
                  }}
                >Next</Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Security;
